// drawer/components/ExploitsTimelineComponent.tsx
import { Section, Timeline } from '@/sections/threats/components';
import type {
  EnrichedVulnerability,
  TimelineEvent,
} from '@/sections/threats/types';

interface ExploitsTimelineComponentProps {
  threatIntel?: EnrichedVulnerability;
}

/**
 * Exploitation timeline component that builds events from vulnerability data
 */
export function ExploitsTimelineComponent({
  threatIntel,
}: ExploitsTimelineComponentProps) {
  const timelineEvents: TimelineEvent[] = [];

  // Add KEV date added
  const kevAdded =
    threatIntel?.kevDateAdded ||
    (threatIntel?.exploits?.timeline as Record<string, string> | undefined)
      ?.cisa_kev_date_added;
  if (kevAdded) {
    timelineEvents.push({ date: kevAdded, title: 'Added to CISA KEV' });
  }

  // Add NVD Published
  const nvdPublished =
    threatIntel?.created ||
    (threatIntel?.exploits?.timeline as Record<string, string> | undefined)
      ?.nvd_published;
  if (nvdPublished) {
    timelineEvents.push({ date: nvdPublished, title: 'NVD Published' });
  }

  // Add NVD Last Modified
  const nvdModified =
    threatIntel?.updated ||
    (threatIntel?.exploits?.timeline as Record<string, string> | undefined)
      ?.nvd_last_modified;
  if (nvdModified) {
    timelineEvents.push({ date: nvdModified, title: 'NVD Last Modified' });
  }

  // Add KEV due date
  const kevDue =
    threatIntel?.kevDueDate ||
    (threatIntel?.exploits?.timeline as Record<string, string> | undefined)
      ?.cisa_kev_date_due;
  if (kevDue) {
    timelineEvents.push({ date: kevDue, title: 'CISA KEV Due Date' });
  }

  // Sort by date
  const sortedEvents = timelineEvents.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <Section title="Exploitation Timeline">
      <div
        className="flex min-h-[250px] w-full flex-col gap-2"
        data-testid="exploit-timeline"
      >
        <Timeline events={sortedEvents} />
      </div>
    </Section>
  );
}
